import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Sparkles, Users, Clock, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { planning: "Planejamento", in_progress: "Em andamento", completed: "Concluído", archived: "Arquivado" };
const STATUS_COLOR = { planning: "#71717A", in_progress: "#3B82F6", completed: "#10B981", archived: "#F59E0B" };

export default function Trainings() {
  const [items, setItems] = useState([]);
  const [ai, setAi] = useState({ topic: "", audience: "", goal: "", modality: "presencial" });
  const [aiResult, setAiResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/trainings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const runAI = async () => {
    if (!ai.topic || !ai.audience) return toast.error("Preencha tema e público");
    setLoading(true);
    setAiResult(null);
    try {
      const r = await api.post("/trainings/ai-scope", ai);
      setAiResult(r.data);
      toast.success("Escopo gerado pela IA");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar escopo");
    } finally {
      setLoading(false);
    }
  };

  const createFromAI = async () => {
    if (!aiResult) return;
    const r = await api.post("/trainings", {
      title: aiResult.title || `Treinamento — ${ai.topic}`,
      audience: ai.audience,
      audience_size: 0,
      modality: ai.modality,
      hours: Number(aiResult.hours) || 0,
      scope: aiResult.scope || "",
      methodology: aiResult.methodology || "",
      prerequisites: aiResult.prerequisites || "",
      client_name: "",
      location: "",
      starts_at: "",
      instructor_name: "",
      pass_grade: 70,
      pass_attendance: 70,
    });
    setAiOpen(false);
    toast.success("Treinamento criado");
    navigate(`/trainings/${r.data.training_id}`);
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline">Gestão Operacional</p>
          <h1 className="font-heading text-4xl mt-2">Treinamentos</h1>
          <p className="text-sm text-muted-foreground mt-2">Planeje escopos, gerencie turmas e emita certificados com autoridade.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={aiOpen} onOpenChange={setAiOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#D4AF37] hover:bg-[#B8961F] text-white rounded-md" data-testid="ai-scope-btn">
                <Sparkles size={16} className="mr-1" /> Gerar Escopo com IA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                  <Sparkles size={20} className="text-[#D4AF37]" /> Gerador de Escopo por IA
                </DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Tema / Assunto</Label>
                  <Input placeholder="Ex: Liderança situacional" value={ai.topic} onChange={(e) => setAi({ ...ai, topic: e.target.value })} data-testid="ai-topic" />
                </div>
                <div>
                  <Label>Público / Audiência</Label>
                  <Input placeholder="Ex: 50 gerentes de nível médio" value={ai.audience} onChange={(e) => setAi({ ...ai, audience: e.target.value })} data-testid="ai-audience" />
                </div>
                <div className="md:col-span-2">
                  <Label>Objetivo principal (opcional)</Label>
                  <Input placeholder="Ex: melhorar comunicação e delegação" value={ai.goal} onChange={(e) => setAi({ ...ai, goal: e.target.value })} data-testid="ai-goal" />
                </div>
                <div>
                  <Label>Modalidade</Label>
                  <Select value={ai.modality} onValueChange={(v) => setAi({ ...ai, modality: v })}>
                    <SelectTrigger data-testid="ai-modality"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="presencial">Presencial</SelectItem>
                      <SelectItem value="online">Online / Ao vivo</SelectItem>
                      <SelectItem value="híbrido">Híbrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button className="bg-[#C07C88] hover:bg-[#A86571] w-full" onClick={runAI} disabled={loading} data-testid="ai-generate-btn">
                    {loading ? "Gerando…" : <><Sparkles size={16} className="mr-1" /> Gerar Escopo</>}
                  </Button>
                </div>
              </div>

              {aiResult && (
                <div className="mt-4 border-t border-border pt-4 space-y-3 max-h-[50vh] overflow-auto" data-testid="ai-result">
                  <div>
                    <p className="overline mb-1">Título sugerido</p>
                    <p className="font-heading text-xl text-[#C07C88]">{aiResult.title}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><p className="overline mb-1">Carga horária</p><p className="text-sm font-medium">{aiResult.hours} horas</p></div>
                    <div><p className="overline mb-1">Resumo</p><p className="text-sm">{aiResult.summary}</p></div>
                  </div>
                  <div><p className="overline mb-1">Escopo</p><p className="text-sm whitespace-pre-wrap">{aiResult.scope}</p></div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><p className="overline mb-1">Metodologia</p><p className="text-sm">{aiResult.methodology}</p></div>
                    <div><p className="overline mb-1">Pré-requisitos</p><p className="text-sm">{aiResult.prerequisites}</p></div>
                  </div>
                  {aiResult.deliverables && (<div><p className="overline mb-1">Entregáveis</p><p className="text-sm">{aiResult.deliverables}</p></div>)}
                </div>
              )}

              <DialogFooter>
                {aiResult && <Button className="bg-[#C07C88] hover:bg-[#A86571]" onClick={createFromAI} data-testid="ai-create-btn">Criar treinamento com este escopo</Button>}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button asChild variant="outline" data-testid="new-training-btn">
            <Link to="/trainings/new"><Plus size={16} className="mr-1" /> Novo</Link>
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 border-border shadow-none border-dashed text-center">
          <GraduationCap size={40} strokeWidth={1.3} className="mx-auto text-muted-foreground" />
          <p className="font-heading text-2xl mt-4">Sem treinamentos ainda</p>
          <p className="text-sm text-muted-foreground mt-2">Use o Gerador de Escopo por IA para criar seu primeiro programa em segundos.</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((t) => (
            <Link to={`/trainings/${t.training_id}`} key={t.training_id} data-testid={`training-card-${t.training_id}`}>
              <Card className="p-6 border-border shadow-none hover:-translate-y-0.5 hover:shadow-sm transition-all bg-white">
                <div className="flex items-start justify-between">
                  <p className="font-mono text-[10px] text-muted-foreground">{t.training_id.slice(0, 12)}</p>
                  <span className="inline-flex items-center text-[10px] font-medium">
                    <span className="status-dot" style={{ background: STATUS_COLOR[t.status] }} />
                    {STATUS_LABEL[t.status]}
                  </span>
                </div>
                <p className="font-heading text-lg mt-3 line-clamp-2">{t.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.client_name || "Cliente não informado"}</p>
                <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Users size={12} /> {t.participants?.length || 0} inscritos</span>
                  <span className="flex items-center gap-1"><Clock size={12} /> {t.hours || 0}h</span>
                  <span className="uppercase tracking-wider text-[9px]">{t.modality}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
